import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import { AlertCard } from '../../AlertCard';

const DEFAULT_TITLE_KEY = 'experienceV2.slotError.title';
const DEFAULT_BODY_KEY = 'experienceV2.slotError.body';
const RETRY_KEY = 'experienceV2.slotError.retry';

export interface SlotErrorBoundaryProps {
  children: ReactNode;
  /**
   * i18n key for the fallback heading. Defaults to a generic
   * "this panel couldn't be shown" message; pass a slot-specific key
   * (e.g. `experienceV2.document.errorTitle`) where the page already has
   * better copy for the same failure.
   */
  title?: string;
  /** i18n key for the fallback body. See `title`. */
  description?: string;
  /**
   * Called when a child throws. Lets the consuming app forward the crash
   * to its own reporter — this lib has no Sentry dependency of its own,
   * and `sales` has no Sentry at all.
   */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Called when the reader clicks Try again, before the slot re-mounts. */
  onRetry?: () => void;
  /**
   * Clears the error whenever this value changes. Pass whatever identifies
   * the content being rendered — e.g. the document url. Without it a slot
   * that failed on document A keeps showing the error card after the
   * reader picks document B, because switching swaps props in place
   * rather than re-mounting.
   */
  resetKey?: string | number;
}

interface SlotErrorBoundaryState {
  error: Error | null;
  resetKey?: string | number;
}

const SlotErrorFallback = ({
  title,
  description,
  onRetry,
}: {
  title: string;
  description: string;
  onRetry: () => void;
}) => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  return (
    <Flex
      vertical
      align="center"
      justify="center"
      gap={12}
      style={fallbackStyle}
    >
      <AlertCard type="error" title={t(title)} description={t(description)} />
      <Button size="small" shape="round" onClick={onRetry}>
        {t(RETRY_KEY)}
      </Button>
    </Flex>
  );
};

/**
 * SlotErrorBoundary — keeps a crash inside one panel of the experience
 * page instead of letting it take the whole page down.
 *
 * The layout wraps each slot (document, Knowledge Hub, chatbot, sidebar
 * overlay) in one of these, so a document viewer that throws leaves the
 * chat and the add-on tabs usable. The fallback deliberately reuses the
 * same `<AlertCard type="error">` the pages already render when a slot's
 * *data* fails to load, so a render crash and a failed fetch look the
 * same to the reader — which, from their side, they are.
 *
 * The raw `error.message` is never shown: it is developer text, and
 * leaking it is how a reader ended up reading
 * `can't access property "legacy", globalThis.pdfJs is undefined`.
 */
export class SlotErrorBoundary extends Component<
  SlotErrorBoundaryProps,
  SlotErrorBoundaryState
> {
  state: SlotErrorBoundaryState = { error: null };

  static getDerivedStateFromError(
    error: Error,
  ): Partial<SlotErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: SlotErrorBoundaryProps,
    state: SlotErrorBoundaryState,
  ): Partial<SlotErrorBoundaryState> | null {
    if (props.resetKey === state.resetKey) return null;
    // New content in the same slot: drop a stale error so the incoming
    // document/panel gets a real render instead of the previous failure.
    return {
      resetKey: props.resetKey,
      ...(state.error ? { error: null } : {}),
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('SlotErrorBoundary caught:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    // Clearing the error re-mounts the children from scratch, which is
    // what makes this worth offering: most slot failures are a transient
    // fetch or a signed url that has since been refreshed.
    this.setState({ error: null });
    this.props.onRetry?.();
  };

  render() {
    const {
      children,
      title = DEFAULT_TITLE_KEY,
      description = DEFAULT_BODY_KEY,
    } = this.props;

    if (this.state.error) {
      return (
        <SlotErrorFallback
          title={title}
          description={description}
          onRetry={this.handleRetry}
        />
      );
    }

    return children;
  }
}

export default SlotErrorBoundary;

const fallbackStyle = {
  width: '100%',
  height: '100%',
  padding: 24,
} as const;
